import React, { useEffect, useState } from 'react';
import { deleteMember, getMemberPaymentCount } from '../firebase/database';
import { calculateMembershipFees, isMembershipPaused } from '../utils/membershipFees';
import './DeleteMemberDialog.css';

const CONFIRM_WORD = 'DELETE';

/**
 * Confirmation dialog for permanently deleting a member.
 * Deletion cannot be undone, so it requires typing the confirmation word.
 */
function DeleteMemberDialog({ tenantId, member, onCancel, onDeleted }) {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [paymentCount, setPaymentCount] = useState(null);

  const fees = calculateMembershipFees(member);
  const balanceDue = fees.balanceDue;
  const paused = isMembershipPaused(member);
  const canDelete = confirmText.trim().toUpperCase() === CONFIRM_WORD && !isDeleting;

  // Show how many payment records will be kept
  useEffect(() => {
    let cancelled = false;

    const loadCount = async () => {
      const result = await getMemberPaymentCount(tenantId, member.id);
      if (!cancelled) setPaymentCount(result.count);
    };

    loadCount();
    return () => {
      cancelled = true;
    };
  }, [tenantId, member.id]);

  // Escape closes the dialog unless a delete is in flight
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape' && !isDeleting) onCancel();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isDeleting, onCancel]);

  const handleDelete = async () => {
    if (!canDelete) return;

    setIsDeleting(true);
    setError(null);

    const result = await deleteMember(tenantId, member.id);

    if (result.success) {
      onDeleted(result);
    } else {
      setError(result.message || 'Failed to delete member');
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="delete-dialog-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isDeleting) onCancel();
      }}
    >
      <div className="delete-dialog" role="alertdialog" aria-labelledby="delete-dialog-title">
        <div className="delete-dialog-header">
          <h3 id="delete-dialog-title">🗑️ Delete Member</h3>
        </div>

        <div className="delete-dialog-body">
          <p className="delete-dialog-warning">
            This permanently deletes <strong>{member.fullName || 'this member'}</strong>. It cannot
            be undone.
          </p>

          <div className="delete-dialog-facts">
            <div className="delete-fact">
              <span className="delete-fact-label">Membership ID</span>
              <span className="delete-fact-value">{member.membershipId || '—'}</span>
            </div>
            <div className="delete-fact">
              <span className="delete-fact-label">Phone</span>
              <span className="delete-fact-value">{member.phone || '—'}</span>
            </div>
            <div className="delete-fact">
              <span className="delete-fact-label">Status</span>
              <span className="delete-fact-value">{paused ? '⏸️ Paused' : '✅ Active'}</span>
            </div>
            <div className="delete-fact">
              <span className="delete-fact-label">Outstanding Balance</span>
              <span className={`delete-fact-value ${balanceDue > 0 ? 'danger' : ''}`}>
                ₹{balanceDue.toLocaleString()}
              </span>
            </div>
          </div>

          {balanceDue > 0 && (
            <div className="delete-dialog-balance-warning">
              ⚠️ This member still owes <strong>₹{balanceDue.toLocaleString()}</strong>. Deleting
              them removes this outstanding balance from your dashboard totals.
            </div>
          )}

          <p className="delete-dialog-note">
            {paymentCount === null
              ? 'Checking payment records...'
              : paymentCount === 0
                ? 'This member has no payment records.'
                : `Their ${paymentCount} payment record${paymentCount === 1 ? '' : 's'} will be kept so past revenue reporting stays accurate.`}
          </p>

          <label className="delete-dialog-confirm-label" htmlFor="deleteConfirmInput">
            Type <strong>{CONFIRM_WORD}</strong> to confirm:
          </label>
          <input
            id="deleteConfirmInput"
            type="text"
            className="delete-dialog-input"
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            placeholder={CONFIRM_WORD}
            autoComplete="off"
            disabled={isDeleting}
          />

          {error && <div className="delete-dialog-error">{error}</div>}
        </div>

        <div className="delete-dialog-footer">
          <button
            type="button"
            className="delete-dialog-cancel"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="delete-dialog-confirm"
            onClick={handleDelete}
            disabled={!canDelete}
          >
            {isDeleting ? 'Deleting...' : 'Delete Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteMemberDialog;
